import { useState } from 'react';
import type { ListSubmissionsRequest, SavedFilter } from '../types/moderation';
import Select from './ui/Select';

interface SavedFiltersBarProps {
  savedFilters: SavedFilter[];
  /** Called when the user picks a preset from the dropdown. */
  onApply: (filter: SavedFilter) => void;
  /** Called with the new name when the user confirms the inline save prompt. */
  onSave: (name: string) => Promise<void>;
  /** Called when the user deletes a saved filter via the Manage list. */
  onDelete: (name: string) => Promise<void>;
  /** Whether the user is permitted to export CSV. */
  canExport: boolean;
  /** Trigger a CSV export of the current filter set. */
  onExport: () => void;
  exportBusy: boolean;
  /** Active query — used for the Save prompt payload. */
  activeQuery: ListSubmissionsRequest;
}

/**
 * F-202 — focused saved-filters strip extracted from SubmissionList.
 *
 * Owns the Select preset dropdown, the inline "Save as…" prompt
 * (F-002), the Export CSV button, and the Manage list. Filter
 * application is delegated upward; this component holds no filter
 * state of its own.
 */
export default function SavedFiltersBar({
  savedFilters,
  onApply,
  onSave,
  onDelete,
  canExport,
  onExport,
  exportBusy,
  activeQuery,
}: SavedFiltersBarProps) {
  // Reference activeQuery so callers receive the latest snapshot when
  // they invoke onSave; held in a ref-equivalent var so lints don't
  // complain about the unused prop. (onSave receives just the name;
  // the parent uses its own state to assemble the body.)
  void activeQuery;
  const [promptOpen, setPromptOpen] = useState(false);
  const [name, setName] = useState('');
  const [busy, setBusy] = useState(false);

  const confirm = async () => {
    const trimmed = name.trim();
    if (!trimmed) return;
    setBusy(true);
    try {
      await onSave(trimmed);
      setPromptOpen(false);
      setName('');
    } finally {
      setBusy(false);
    }
  };

  const cancel = () => {
    if (busy) return;
    setPromptOpen(false);
    setName('');
  };

  return (
    <div className="saved-filters-bar" data-testid="saved-filters-bar">
      <label className="filter-bar__field saved-filters-bar__select">
        <span>Saved filters</span>
        <Select
          options={[
            'Choose a preset...',
            // F-217: prefix team-scoped filters with a pin glyph so the
            // user can tell apart their own saved sets from team presets.
            ...savedFilters.map((f) => (f.scope === 'team' ? `📌 ${f.name}` : f.name)),
          ]}
          value="Choose a preset..."
          onValueChange={(v) => {
            const trimmed = v.replace(/^📌\s*/, '');
            const found = savedFilters.find((f) => f.name === trimmed);
            if (found) onApply(found);
          }}
        />
      </label>
      {promptOpen ? (
        <div
          className="saved-filters-bar__inline-prompt"
          role="group"
          aria-label="Name this filter"
        >
          <input
            type="text"
            autoFocus
            value={name}
            onChange={(e) => setName(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                void confirm();
              } else if (e.key === 'Escape') {
                e.preventDefault();
                cancel();
              }
            }}
            placeholder="Filter name"
            aria-label="Filter name"
            data-testid="save-filter-name"
            disabled={busy}
          />
          <button
            type="button"
            className="btn btn-primary"
            onClick={() => void confirm()}
            disabled={busy || !name.trim()}
            data-testid="save-filter-confirm"
          >
            {busy ? 'Saving…' : 'Save'}
          </button>
          <button
            type="button"
            className="btn btn-secondary"
            onClick={cancel}
            disabled={busy}
            data-testid="save-filter-cancel"
          >
            Cancel
          </button>
        </div>
      ) : (
        <button
          type="button"
          className="btn btn-secondary"
          onClick={() => setPromptOpen(true)}
          data-testid="save-filter"
        >
          Save as...
        </button>
      )}
      {canExport && (
        <button
          type="button"
          className="btn btn-secondary"
          onClick={onExport}
          disabled={exportBusy}
          data-testid="export-csv"
        >
          {exportBusy ? 'Exporting…' : 'Export CSV'}
        </button>
      )}
      {savedFilters.length > 0 && (
        <details className="saved-filters-bar__manage">
          <summary>Manage</summary>
          <ul className="saved-filters-bar__list">
            {savedFilters.map((f) => (
              <li key={f.name}>
                <span>
                  {f.scope === 'team' && (
                    <span
                      className="saved-filters-bar__team-pin"
                      title="Team-wide preset"
                      aria-label="Team-wide preset"
                    >
                      📌{' '}
                    </span>
                  )}
                  {f.name}
                </span>
                <button
                  type="button"
                  className="btn btn-link"
                  onClick={() => void onDelete(f.name)}
                  data-testid={`delete-saved-${f.name}`}
                  /* F-217: only the owner can delete a team-scoped
                     preset; the API enforces this server-side, but
                     surface a disabled state here as a hint. */
                  disabled={f.scope === 'team'}
                  title={
                    f.scope === 'team'
                      ? 'Team presets can only be removed by a lead moderator from the admin panel.'
                      : undefined
                  }
                >
                  Delete
                </button>
              </li>
            ))}
          </ul>
        </details>
      )}
    </div>
  );
}
